import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DefaultL1ObjectManager,
  L1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import { L1Object } from 'core/l1Object/L1Object';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { Order } from 'core/order/Order';
import { AddonFeatureManager, PermissionItem } from 'core';
import {
  DefaultStateModalModel,
  StateModalModel
} from 'containers/Common/StateModalModel';
import _ from 'lodash';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  L1ObjectChannelDetailModel,
  L1ObjectComponentsData,
  getL1ObjectChannelDetailModel
} from './L1ObjectChannelDetailModel';
import { History } from 'history';
import { useCoreContext } from 'contexts/coreContext';
import { ModalStateFactory } from 'containers/Common/ModalStateFactory';
import { TrackingData } from 'core/rtbCampaign/TrackingData';
import { LimitationManagementContext } from './LimitationManagement/LimitationManagementContext';
import { ScheduleData } from 'core/rtbCampaign/ScheduleData';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { hasWeatherLimitation } from 'utils/LimitationUtil';

export type L1ObjectDetailModelData = {
  viewData: any;
  loading: boolean;
  currentUrl: string;
  l1Object?: L1Object;
  creativeManageModal?: StateModalModel;
  trackingManagementModal?: StateModalModel;
  limitationManagementModal?: StateModalModel;
  scheduleManagementModal?: StateModalModel;
  redirectPath?: string;
  componentsData?: L1ObjectComponentsData;
  l2ObjectList: any[];
  /** 綁了天氣鎖定的活動數，機台投遞驗證 Tab 的 badge 用；與報表的查詢區間無關 */
  weatherTargetedCampaignCount: number;
  modifyPermissionItem: PermissionItem;
  canDonwloadRtbReport: boolean;
  canShowOutdoorDeviceReport: boolean;
  showDownloadRtbReportModal: boolean;
  showOutdoorDeviceReport: boolean;
  setShowDownloadRtbReportModal: (show: boolean) => void;
  setShowOutdoorDeviceReport: (show: boolean) => void;
};

const defaultL1ObjectManager = new DefaultL1ObjectManager();

export const useL1ObjectDetailModel = (
  l1ObjectId: number | string,
  order: Order,
  addonFeatureManager: AddonFeatureManager,
  l1ObjectManager: L1ObjectManager = defaultL1ObjectManager
): L1ObjectDetailModelData => {
  const [l1Object, setL1Object] = useState<L1Object | undefined>(undefined);
  const [redirectPath, setRedirectPath] = useState<string | undefined>(
    undefined
  );
  const { loading, callAPIs } = useCallAPI();
  const [channelDetailModel, setChannelDetailModel] = useState<
    L1ObjectChannelDetailModel | undefined
  >();
  const [showDownloadRtbReportModal, setShowDownloadRtbReportModal] =
    useState(false);
  const [showOutdoorDeviceReport, setShowOutdoorDeviceReport] = useState(false);

  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const core = useCoreContext();
  const localeMeta = _.get(core, 'accountManager.localeMeta');

  const fetchL1Object = useCallback(
    async (onFinish?: () => void) => {
      callAPIs(
        [l1ObjectManager.getL1Object.bind(l1ObjectManager, l1ObjectId)],
        async (l1Object) => {
          // 使用 window.location.search 而非 location.search，避免閉包捕獲舊值
          const query = new URLSearchParams(window.location.search);
          const searchString = query.get('search');
          const channelDetailModel = getL1ObjectChannelDetailModel(
            order,
            l1Object,
            addonFeatureManager,
            fetchL1Object,
            localeMeta
          );
          if (l1Object.adsOrderId !== order.id || !channelDetailModel) {
            const redirectPath = `/orders/${order.orderNumber}/campaign-groups/${l1Object.l1ObjectId}/error404`;
            setRedirectPath(redirectPath);
            onFinish?.();
            return;
          }
          setL1Object(l1Object);

          callAPIs(
            [
              channelDetailModel.initChannelModel.bind(
                channelDetailModel,
                searchString
              )
            ],
            () => {
              setChannelDetailModel(channelDetailModel);
              onFinish?.();
            }
          );
        }
      );
    },
    [
      callAPIs,
      l1ObjectManager,
      l1ObjectId,
      order,
      addonFeatureManager,
      localeMeta
    ]
  );

  const invokeFetchL1Object = useCallback(
    (invoke: boolean) => {
      invoke && fetchL1Object();
    },
    [fetchL1Object]
  );

  useEffect(() => {
    invokeFetchL1Object(!match.isExact && l1Object === undefined);
  }, [l1Object, invokeFetchL1Object, location.pathname, match.isExact]);

  useEffect(() => {
    invokeFetchL1Object(match.isExact);
  }, [invokeFetchL1Object, match.isExact]);

  const creativeManageModal = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const factoryModel = getCreativeManagementStateFactory(
      query,
      l1Object,
      channelDetailModel,
      history
    );
    if (!factoryModel) {
      return;
    }

    return new DefaultStateModalModel(factoryModel, (dirty) => {
      history.replace(location.pathname);
      invokeFetchL1Object(dirty);
    });
  }, [
    location.search,
    location.pathname,
    invokeFetchL1Object,
    history,
    l1Object,
    channelDetailModel
  ]);

  const trackingManagementModal = useMemo(() => {
    if (!l1Object || l1Object.channel !== ChannelGroupSetting.RTB.key) {
      return undefined;
    }

    const query = new URLSearchParams(location.search);
    const factoryModel = getTrackingManagementStateFactory(
      query,
      l1Object,
      channelDetailModel
    );
    if (!factoryModel) {
      return;
    }
    return new DefaultStateModalModel(factoryModel, (dirty) => {
      history.replace(location.pathname);
      invokeFetchL1Object(dirty);
    });
  }, [
    location.search,
    location.pathname,
    invokeFetchL1Object,
    history,
    l1Object,
    channelDetailModel
  ]);

  const limitationManagementModal = useMemo(() => {
    if (!l1Object) {
      return undefined;
    }
    const query = new URLSearchParams(location.search);
    const factoryModel = getLimitationManagementStateFactory(
      query,
      l1Object,
      channelDetailModel
    );
    if (!factoryModel) {
      return;
    }
    return new DefaultStateModalModel(factoryModel, (dirty) => {
      history.replace(location.pathname);
      invokeFetchL1Object(dirty);
    });
  }, [
    location.search,
    location.pathname,
    invokeFetchL1Object,
    history,
    l1Object,
    channelDetailModel
  ]);

  const scheduleManagementModal = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const factoryModel = getScheduleManagementStateFactory(
      query,
      l1Object,
      channelDetailModel
    );
    if (!factoryModel) {
      return;
    }
    return new DefaultStateModalModel(factoryModel, (dirty) => {
      history.replace(location.pathname);
      invokeFetchL1Object(dirty);
    });
  }, [
    location.search,
    location.pathname,
    invokeFetchL1Object,
    history,
    l1Object,
    channelDetailModel
  ]);

  const viewData = useMemo(() => {
    if (!l1Object || !channelDetailModel) {
      return;
    }
    return _.omitBy(
      {
        basic: channelDetailModel.getL1ObjectBasicViewData(),
        channelData: channelDetailModel.getL1ObjectChannelViewData()
      },
      _.isUndefined
    );
  }, [l1Object, channelDetailModel]);

  const canDonwloadRtbReport = useMemo(() => {
    if (!l1Object) {
      return false;
    }
    if (l1Object.channel === ChannelGroupSetting.RTB.key) {
      return true;
    }
    if (l1ObjectManager.isOutdoorChannel(l1Object.channel)) {
      return true;
    }
    return false;
  }, [l1Object, l1ObjectManager]);

  const l2ObjectList: any[] = _.get(channelDetailModel, 'l2ObjectList', []);

  return {
    loading,
    l1Object: l1Object,
    currentUrl: match.url,
    viewData,
    canDonwloadRtbReport,
    creativeManageModal,
    trackingManagementModal,
    limitationManagementModal,
    scheduleManagementModal,
    redirectPath,
    componentsData: _.get(channelDetailModel, 'componentsData'),
    l2ObjectList,
    weatherTargetedCampaignCount: l2ObjectList.filter((l2Object) =>
      hasWeatherLimitation(l2Object.additionalInfo?.limitations)
    ).length,
    modifyPermissionItem: l1ObjectManager.getModifyPermissionItem(l1Object),
    canShowOutdoorDeviceReport: l1Object
      ? l1ObjectManager.isOutdoorChannel(l1Object.channel)
      : false,
    showDownloadRtbReportModal,
    showOutdoorDeviceReport,
    setShowDownloadRtbReportModal,
    setShowOutdoorDeviceReport
  };
};

const getCreativeManagementStateFactory = (
  query: URLSearchParams,
  l1Object: L1Object | undefined,
  channelDetailModel: L1ObjectChannelDetailModel | undefined,
  history: History
) => {
  const draftIdsParam = query.get('draftIds');
  const campaignIdsParam = query.get('campaignIds');
  const actionParam = query.get('action');
  if (!actionParam || (!campaignIdsParam && !draftIdsParam) || !l1Object) {
    return undefined;
  }

  if (!channelDetailModel) {
    return undefined;
  }

  const campaignIds = _.defaultTo(campaignIdsParam, '').split(',');
  const draftIds = _.defaultTo(draftIdsParam, '').split(',');
  return channelDetailModel.getCreativeManagementStateFactory(
    campaignIds,
    draftIds,
    actionParam,
    history
  );
};

const getTrackingManagementStateFactory = (
  query: URLSearchParams,
  l1Object: L1Object | undefined,
  channelDetailModel: L1ObjectChannelDetailModel | undefined
): ModalStateFactory<TrackingData> | undefined => {
  const campaignIdsParam = query.get('campaignIds');
  const actionParam = query.get('action');
  if (
    !actionParam ||
    !campaignIdsParam ||
    !l1Object ||
    actionParam !== 'batchEditTracking'
  ) {
    return undefined;
  }

  if (!channelDetailModel) {
    return undefined;
  }

  const campaignIds = _.defaultTo(campaignIdsParam, '').split(',');
  return channelDetailModel.getTrackingManagementStateFactory
    ? channelDetailModel.getTrackingManagementStateFactory(campaignIds)
    : undefined;
};

const getLimitationManagementStateFactory = (
  query: URLSearchParams,
  l1Object: L1Object | undefined,
  channelDetailModel: L1ObjectChannelDetailModel | undefined
): ModalStateFactory<LimitationManagementContext> | undefined => {
  const campaignIdsParam = query.get('campaignIds');
  const actionParam = query.get('action');
  if (!actionParam || !campaignIdsParam || !l1Object) {
    return undefined;
  }

  if (!channelDetailModel) {
    return undefined;
  }

  const campaignIds = _.defaultTo(campaignIdsParam, '').split(',');
  return channelDetailModel.getLimitationManagementStateFactory
    ? channelDetailModel.getLimitationManagementStateFactory(
        campaignIds,
        actionParam
      )
    : undefined;
};

const getScheduleManagementStateFactory = (
  query: URLSearchParams,
  l1Object: L1Object | undefined,
  channelDetailModel: L1ObjectChannelDetailModel | undefined
): ModalStateFactory<ScheduleData> | undefined => {
  const draftIdsParam = query.get('draftIds');
  const campaignIdsParam = query.get('campaignIds');
  const actionParam = query.get('action');
  if (
    !actionParam ||
    (!campaignIdsParam && !draftIdsParam) ||
    !l1Object ||
    actionParam !== 'batchEditSchedule'
  ) {
    return undefined;
  }

  if (!channelDetailModel) {
    return undefined;
  }

  const campaignIds = _.defaultTo(campaignIdsParam, '').split(',');
  return channelDetailModel.getScheduleManagementStateFactory
    ? channelDetailModel.getScheduleManagementStateFactory(campaignIds)
    : undefined;
};
